/**
 * @file Quick-payment dialog — creates one payment for a single charge.
 * A charge whose class differs from the chosen payment class is refused before saving.
 */
import * as React from "react";
import Component from "../../AbstractComponent";

import { PromptDialog } from "./PromptDialog";
import { DatePickerComponent } from "../DatePicker/DatePickerComponent";
import { ChargeInterface } from "../../../Entity/Entities/Charge/ChargeInterface";
import { PAYMENT_ROUTE } from "../../../Entity/Entities/Charge/PaymentRoute";
import {
  getBaseBankAccountDefaults,
  getBaseBankAccountFields,
  getBaseBankAccountPresentation,
} from "../../../Entity/Entities/Payment/BaseBankAccount";
import { BankAccountInterface } from "../../../Entity/Entities/BankAccount/BankAccountInterface";
import { PaymentTypeInterface } from "../../../Entity/Entities/Payment/PaymentType/PaymentTypeInterface";
import Searchbar from "../Searchbar/Searchbar";
import Time from "../../../Time/Time";
import Constants from "../../../Action/Constants";
import  PaymentClassContainer from "../../../Entity/Entities/Payment/PaymentClass/PaymentClassContainer";
import { Company } from "../../../Entity/Entities/Company/Company";
import { Person } from "../../../Entity/Entities/Person/Person";
import TextField from "../Material/TextField";
import Formatter from "../../../Localization/Formatter";
import PaymentTypeContainer from "../../../Entity/Entities/Payment/PaymentType/PaymentTypeContainer";
import Select from "../Material/Select";
import { PageTransition } from "../Spinner/PageTransition";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { PersonInterface } from "../../../Entity/Entities/Person/PersonInterface";
import { CompanyInterface } from "../../../Entity/Entities/Company/CompanyInterface";
import { CoachNewCounterpartyDialog } from "./CoachNewCounterpartyDialog";

export class QuickPaymentDialog extends Component {
  protected timer;

  public state = {
    amountForPayment: null,
    isOpen: false,
    charge: null,
    paymentDateQuickOption: new Date(),
    agencyBankAccountIdForPayment: null,
    baseBankAccountForPayment: "",
    baseBankAccountNameForPayment: "",
    descriptionForPayment: "",
    counterpartyIbanForPayment: "",
    currencyIdForPayment: this.props.app?.settings?.user?.currency?.getCurrencyId(),
    isSaving: false,
    payment: null,
    hasSettlementType: false,
    typeIdForPayment: String(PaymentTypeContainer.pickIdFromLabel("Bank")),
    agencyBankAccounts: [],
    paymentClassId: "",
		selectedCounterpartyForPayment: null,
    counterpartyIbanForPaymentIsManuallyChanged: false,
    openCoachCreateNewCounterparty: false
  };

  constructor(public props) {
    super(props);
  }

  // Derived per call, never stored: it reads the charge and the Redux bank-account list,
  // which both change without this component's own state being rewritten.
  public getBaseAccountPresentation = () => getBaseBankAccountPresentation({
    paymentRouteId: this.state.charge?.paymentRouteId,
    agencyId: this.getAgency()?.id,
    ownerCompanyId: this.state.charge?.ownerId,
    agencyBankAccountCount: this.getAgencyBankAccounts().length,
  });

  public componentWillReceiveProps(nextProps) {
    const agencyBankAccounts: BankAccountInterface[] = this.getAgencyBankAccounts();

    const defaultBankAccount: BankAccountInterface = agencyBankAccounts.find(bankAccount => {
      return bankAccount.isDefault === true;
    });

    let agencyBankAccountId;

    if(!!defaultBankAccount) {
      agencyBankAccountId = !!defaultBankAccount ? defaultBankAccount.id : null;
    } else {
      agencyBankAccountId = !!agencyBankAccounts[0] ? agencyBankAccounts[0].id : null;
    }

    const agencyBankAccountsList = nextProps?.app?.bankAccounts
    .filter((bankAccount: BankAccountInterface) => bankAccount.companyId === this.getAgency()?.id);

    const currentBankAccount = agencyBankAccounts.find((bankAccount: BankAccountInterface) =>
                                                bankAccount.id === agencyBankAccountId);

    this.setState({
      agencyBankAccounts: !!currentBankAccount ?
      [...new Set([currentBankAccount, ...agencyBankAccountsList.filter(bankAccount => !bankAccount.removed)])] :
      agencyBankAccountsList.filter(bankAccount => !bankAccount.removed),
    })


    if (nextProps.isOpen !== this.state.isOpen) {
      this.setState({
        isOpen: nextProps.isOpen,
      });
    }

    if (nextProps.charge !== this.state.charge) {
      this.setState({
        charge: nextProps.charge
      }, () => {
        const chargeClassId: number = this.state.charge?.chargeClass?.id;
        const paymentRouteId = this.state.charge?.paymentRouteId;
        const baseAccountDefaults = getBaseBankAccountDefaults({
          paymentRouteId: paymentRouteId,
          usesSelector: this.getBaseAccountPresentation().usesSelector,
          baseAccountCompany: paymentRouteId === PAYMENT_ROUTE.DIRECT_TRACKED
            ? this.state.charge?.owner
            : this.getAgency(),
          transactionIban: "",
        });

        this.setState({
          amountForPayment: Formatter.simpleFormatNumber(this.state.charge?.amountRemaining),
          agencyBankAccountIdForPayment: agencyBankAccountId,
          baseBankAccountForPayment: baseAccountDefaults.baseBankAccount,
          baseBankAccountNameForPayment: baseAccountDefaults.baseBankAccountName,
          paymentClassId: String(chargeClassId),
          selectedCounterpartyForPayment: this.state.charge?.counterparty,
          descriptionForPayment: this.state.charge?.description
        })
      });
    }
  }

  public componentWillUnmount() {
    this.setState({
      charge: null,
      isOpen: false
    })
  }

  public save = (charge: ChargeInterface) => {
    if (!this.state.charge) {
      return;
    }

    const payment: any = {
      amount: parseFloat(parseFloat(String(Formatter.deleteExtraSpace(this.state.amountForPayment)).replace(",", ".")).toFixed(2)),
      amountRemaining: parseFloat((100 * parseFloat(String(Formatter.deleteExtraSpace(this.state.amountForPayment)).replace(",", "."))).toFixed(2)),
      currencyId: this.state.currencyIdForPayment,
      currency: this.state.currencyIdForPayment,
      firstDateOfStatementPeriod: Time.formatOrNull(charge.firstDate),
      dateOfThePayment: Time.formatOrNull(this.state.paymentDateQuickOption),
      paymentTypeId: Number(this.state.typeIdForPayment),
      ...getBaseBankAccountFields({
        isBankType: Number(this.state.typeIdForPayment) === PaymentTypeContainer.pickIdFromLabel("Bank"),
        presentation: this.getBaseAccountPresentation(),
        selectedBankAccountId: this.state.agencyBankAccountIdForPayment,
        baseBankAccount: this.state.baseBankAccountForPayment,
        baseBankAccountName: this.state.baseBankAccountNameForPayment,
      }),
      paymentClassId: Number(this.state.paymentClassId),
      description: this.state.descriptionForPayment
    };

    if (!!charge && charge.chargeClass.id !== payment.paymentClassId) {
      this.setState({ hasModifiedData: true, isSaving: false });

      return this.props.dispatch({
        type: Constants.SNACKBAR_ADD_MESSAGE,
        payload: {
          message: this.trans("preprocess/cannotpaycharge-info/differentclasses"),
          type: "warning"
        }
      });
    }

    if (!!charge) {
      payment.chargeIds = [charge.id];
    }

    if (this.state.selectedCounterpartyForPayment instanceof Person) {
      payment.contactId = this.state.selectedCounterpartyForPayment?.id;
    }

    if (this.state.selectedCounterpartyForPayment instanceof Company) {
      payment.companyId = this.state.selectedCounterpartyForPayment?.id;
    }
  
    this.saving(() => {
      this.props.dispatch({
        type: Constants.GLOBAL_PAYMENT_CREATE_START,
        payload: {
          payment: payment
        },
        onSuccess: () => {
          this.props.dispatch({
            type: Constants.SAVED_DATA_BEFORE_REDIRECTING_CLEAR
          });
          this.setState({isSaving: false}, () => {
            this.clearState();
            this.props.closeDialog();
            this.props.initializePage(); 
          })
      
        },
        onError: () => {
          this.setState({
            isSaving: false
          });
          // this.props.dispatch({
          //   type: Constants.SNACKBAR_ADD_MESSAGE,
          //   payload: {
          //     message: this.trans("cannotpaycharge-info/bankaccountmissing"),
          //     type: "error"
          //   }
          // });
        }
      });
    });
  }

  public clearState = () => {
    this.setState({
      amountForPayment: null,
      isOpen: false,
      charge: null,
      paymentDateQuickOption: new Date(),
      agencyBankAccountIdForPayment: null,
      baseBankAccountForPayment: "",
      baseBankAccountNameForPayment: "",
      descriptionForPayment: "",
      counterpartyIbanForPayment: "",
      currencyIdForPayment: this.props.app?.settings?.user?.currency?.getCurrencyId(),
      isSaving: false,
      payment: null,
      hasSettlementType: false,
      typeIdForPayment: String(PaymentTypeContainer.pickIdFromLabel("Bank")),
      agencyBankAccounts: [],
      paymentClassId: "",
      selectedCounterpartyForPayment: null,
      counterpartyIbanForPaymentIsManuallyChanged: false
    })
  }

  public selectDate = (date: Date, identifier: string): void => {
    this.setState({
      [identifier]: date, // todo fix hardcoded value
    });
  }

  public changeAmountForPayment = (event): void => {
    let amount: string = event.target.value;
    amount = this.formatter.formatSigns(amount);

    this.setState({
      amountForPayment: amount,
      additionalSectionHasModifiedData: true
    });
  }

  public changeCurrencyIdForCharge = (event): void => {
    let currency: number = event.target.value;

    this.setState({
      currencyIdForCharge: currency,
      hasModifiedData: true
    });
  }

  public changeTypeIdForPayment = (event): void => {
    const type = String(event.target.value);

    this.setState({
      typeIdForPayment: type,
      additionalSectionHasModifiedData: true,
    });
  }


  public updateAgencyBankAccountIdForPayment = (event): void => {
    const bankAccountId: number = event.target.value;

    if (bankAccountId) {
      this.setState({
        agencyBankAccountIdForPayment: bankAccountId,
        additionalSectionHasModifiedData: true
      });
    }
  }

  public changePaymentClassId = (event): void => {
    const paymentClassId = String(event.target.value);

    this.setState({
      paymentClassId: paymentClassId,
    });
  }

  public selectCounterpartyForPayment = (entity: CompanyInterface | PersonInterface): void => {
    this.setState({
      selectedCounterpartyForPayment: entity,
      hasModifiedData: true
    });
  }

  public clearCounterpartyForPayment = (): void => {
    this.setState({
      selectedCounterpartyForPayment: null,
      hasModifiedData: true
    });
  }

  public changeCounterpartyIbanForPayment = (event): void => {
    const counterpartyIbanForPayment: string = event.target.value;

    this.setState({
      counterpartyIbanForPayment: counterpartyIbanForPayment,
      counterpartyIbanForPaymentWasChanged: true,
      additionalSectionHasModifiedData: true
    });
  }

  public changeDescriptionForPayment = (event): void => {
    const description: string = event.target.value;
    this.setState({
      descriptionForPayment: description,
      descriptionForPaymentIsManuallyChanged: true,
    });
  }

  public changeBaseBankAccountForPayment = (event): void => {
    const baseBankAccount: string = event.target.value;
    this.setState({
      baseBankAccountForPayment: baseBankAccount,
      additionalSectionHasModifiedData: true,
    });
  }

  public changeBaseBankAccountNameForPayment = (event): void => {
    const baseBankAccountName: string = event.target.value;
    this.setState({
      baseBankAccountNameForPayment: baseBankAccountName,
      additionalSectionHasModifiedData: true,
    });
  }

  public renderContent = () => {
    return (
      <div style={{width: "420px"}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
        <div style={{display: "flex", flexDirection: "column", width: "70%", marginRight: "10px"}}>
          <TextField
            // id="аmountForPayment"
            type="text"
            value={this.state.amountForPayment}
            onChange={this.changeAmountForPayment}
            autoFocus={true}
            className="form-size scroll-to-amountForPayment"
            style={{marginRight: "30px"}}
            label={this.trans("section/payment-field/аmount-label")}
            helperText={this.getErrorFor("amountForPayment")}
            error={!!this.getErrorFor("amountForPayment")}
          />
          {/* {!!this.getErrorFor("amountForPayment") &&  <div className="component-error-text">{this.getErrorFor("amountForPayment")}</div>} */}
          </div>

          <Select
            className="form-input scroll-to-currencyIdForCharge"
            style={{width: "30%"}}
            error={this.getErrorFor("currencyIdForCharge")}
            value={this.state.currencyIdForPayment}
            onChange={this.changeCurrencyIdForCharge}
            htmlFor="currencyIdForCharge"
            name="currencyIdForCharge" 
            id="currencyIdForCharge"
          >
            {this.getCurrencySelectboxes()}
          </Select>
        </div>

        <DatePickerComponent
          {...this.props}
          id="firstDateOfStatementPeriodForPayment"
          name="firstDateOfStatementPeriodForPayment"
          className="scroll-to-firstDateOfStatementPeriodForPayment"
          label={this.trans("payment-date")}
          initialDate={this.state.paymentDateQuickOption}
          onChange={date => this.selectDate(date, "paymentDateQuickOption")}
          clearButton={true}
          error={this.getErrorFor("firstDateOfStatementPeriodForPayment")}
        />
        <div>
             {
						!this.state.hasSettlementType && PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.state?.payment?.paymentType?.id &&
            (
              <RadioGroup
                name="typeFilters"
                className="filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
									PaymentTypeContainer.getInstances()
										.filter((paymentType: PaymentTypeInterface) => {
                      return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Settlement") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque")  
										})
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

					{
						!this.state.hasSettlementType && !(PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.state?.payment?.paymentType?.id) &&
            (
              <RadioGroup
                name="typeFilters"
                className="filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
									PaymentTypeContainer.getInstances()
										.filter((paymentType: PaymentTypeInterface) => {
                      return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Settlement") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Savings_account")
 
										})
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

          {
						this.state.hasSettlementType && !(PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.state?.payment?.paymentType?.id) &&
            (
              <RadioGroup
                name="typeFilters"
                className="filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
                  PaymentTypeContainer.getInstances()
                  .filter((paymentType: PaymentTypeInterface) => {
                    return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque")  
                    && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Savings_account")
                    })
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

					<TextField
						autoComplete="off"
						id="descriptionForPayment"
						name="descriptionForPayment"
						className="form-element full-size scroll-to-descriptionForPayment"
						type="text"
						label={this.trans("section/payment-sub/banktransaction-field/description-label") + this.trans("field/optional")}
						value={this.state.descriptionForPayment}
						onChange={this.changeDescriptionForPayment}
						variant="standard"
						multiline={true}
						rowsMax={10}
						error={!!this.getErrorFor("descriptionForPayment")}
					/>
					{!!this.getErrorFor("descriptionForPayment") && <div className="component-error-text">{this.getErrorFor("descriptionForPayment")}</div>}

					{/* The counterparty comes first: a base account placed above it reads as the
					    counterparty's, when it is our own side of the payment. */}
					<div>
						<div className="search-bar-container">
              <Searchbar
                {...this.props}
                id="selectedCounterpartyForPayment"
                className={"scroll-to-counterpartyId"}
                // floatingLabelText={labelText}
                floatingLabelText={Number(this.state.paymentClassId) == PaymentClassContainer.pickIdFromLabel("Receivable") ? this.trans("section/charge-field/from-label") : this.trans("section/charge-field/to-label")}
                allResults={true}
                objectField={true}
                onChange={this.selectCounterpartyForPayment}
                onClear={this.clearCounterpartyForPayment}
                selectedEntity={this.state.selectedCounterpartyForPayment}
                searchParameter={"contactsSearch"}
                error={this.getErrorFor("counterpartyId")}
                searchText={this.state.selectedCounterpartyForPayment ? `${this.state.selectedCounterpartyForPayment.nameOfEntity}, ${this.state.selectedCounterpartyForPayment.displayName}` : null}
                styleInModal={"pick-object-modal"}
                createButton={"dropdown-button/newcounterparty"}
                openCoachCreateNewEntity={() => {
                  this.setState({
                    openCoachCreateNewCounterparty: true,
                    selectedCounterparty: null
                  })}
                }
              />
            </div>
					</div>

					{Number(this.state.typeIdForPayment) === PaymentTypeContainer.pickIdFromLabel("Bank")  && (
						this.getBaseAccountPresentation().usesSelector ? (
						<Select
							className="form-element full-size form-input bank-account-wrapper scroll-to-agencyBankAccountIdForPayment correct-label-position"
							variant="standard"
							error={this.getErrorFor("agencyBankAccountIdForPayment")}
							name="agencyBankAccountIdForPayment"
							// id="agencyBankAccountIdForPayment"
							value={!!this.state.agencyBankAccountIdForPayment ? this.state.agencyBankAccountIdForPayment : ""}
							onChange={this.updateAgencyBankAccountIdForPayment}
							htmlFor="agencyBankAccountIdForPayment"
							label={this.trans("section/payment-field/bankaccount-label")}
						>
							{
								this.state.agencyBankAccounts
									.map((bankAccount: BankAccountInterface, key: number) => {
										return (
											<MenuItem
												key={`bankAccount-${key}`}
												value={bankAccount.id}
												className="filter-payment-item"
											>
											<div className="bank-account-menuitem-wrapper" style={{height: "23px"}}>
												<div>{`${bankAccount.accountId ? `${bankAccount.accountId}` : ""} ${bankAccount.name !== "" && bankAccount.name ? ` - ${bankAccount.name}` : ""}`}</div>
											</div>
											</MenuItem>
										);
									})
							}
						</Select>
						) : (
						<React.Fragment>
							{/* Names whose account the two fields below are. Skipped without a resolvable
							    owner, which would leave a label with nothing after the colon. */}
							{this.getBaseAccountPresentation().baseAccountIsOwner && !!this.state.charge?.owner?.displayName && (
								<div className="base-account-owner">
									{`${Number(this.state.paymentClassId) === PaymentClassContainer.pickIdFromLabel("Receivable")
										? this.trans("section/charge-field/to-label")
										: this.trans("section/charge-field/from-label")}: ${this.state.charge.owner.displayName}`}
								</div>
							)}
							<TextField
								autoComplete="off"
								id="baseBankAccountForPayment"
								name="baseBankAccountForPayment"
								className="form-element full-size scroll-to-baseBankAccountForPayment"
								type="text"
								label={this.trans("section/payment-field/bankaccount-label")}
								value={this.state.baseBankAccountForPayment}
								onChange={this.changeBaseBankAccountForPayment}
								variant="standard"
								error={!!this.getErrorFor("baseBankAccountForPayment")}
							/>
							{!!this.getErrorFor("baseBankAccountForPayment") && <div className="component-error-text">{this.getErrorFor("baseBankAccountForPayment")}</div>}
							<TextField
								autoComplete="off"
								id="baseBankAccountNameForPayment"
								name="baseBankAccountNameForPayment"
								className="form-element full-size scroll-to-baseBankAccountNameForPayment"
								type="text"
								label={this.trans("section/payment-field/bankaccountname-label")}
								value={this.state.baseBankAccountNameForPayment}
								onChange={this.changeBaseBankAccountNameForPayment}
								variant="standard"
								error={!!this.getErrorFor("baseBankAccountNameForPayment")}
							/>
							{!!this.getErrorFor("baseBankAccountNameForPayment") && <div className="component-error-text">{this.getErrorFor("baseBankAccountNameForPayment")}</div>}
						</React.Fragment>
						)
					)}

          </div>
      </div>
    );
  }

  public render() {
    return (
      <div>
        <PromptDialog
          {...this.props}
          openLeavePrompt={this.state.isOpen}
          title={this.trans("page/chargeupdate-preprocess/paythischarge-title")}
          subtitle={this.state.charge?.displayName || ""}
          content={this.renderContent()}
          positiveButtonLabel={this.trans("dialog-button/finish")}
          negativeButtonLabel={this.trans("dialog-button/cancel")}
          onSave={() => this.save(this.state.charge)}
          onDiscard={() => {
              this.clearState();
              this.props.closeDialog();
            }
          }
        />

        <CoachNewCounterpartyDialog
          {...this.props}
          openCoachCreateNewCounterparty={this.state.openCoachCreateNewCounterparty}
          onCloseCounterpartyCoach={entity => {
            if(entity) {
              this.setState({
                openCoachCreateNewCounterparty: false            
              }, () => {this.selectCounterpartyForPayment(entity)})
            } else {
              this.setState({
                openCoachCreateNewCounterparty: false  
              })          
            }
          }}
        />

        <PageTransition isSaving={this.state.isSaving} />
      </div>
    );
  }
}
